<template>
  <div class="neuro-container">
    <canvas ref="canvasEl" id="neuro"></canvas>
    <!-- The canvas element is the only visible part -->
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, toRefs, computed, watch, reactive } from "vue";

// Component props
const props = defineProps({
  // Color object with r, g, b values between 0-1
  baseColor: {
    type: Object,
    default: () => ({ r: 0.5, g: 0.2, b: 0.8 }), // Default purple
  },
  // Transition duration in milliseconds
  transitionDuration: {
    type: Number,
    default: 1000,
  },
  // Transition speed factor (0.01-0.2, higher = faster transition)
  transitionSpeed: {
    type: Number,
    default: 0.08,
  },
  // Noise settings
  noiseSettings: {
    type: Object,
    default: () => ({
      scale: 8.0, // Initial scale of the noise
      layers: 15, // Number of noise layers
      power: 3.0, // Noise power (higher = more contrast)
      intensity: 1.2, // Noise intensity multiplier
    }),
  },
});

// Get reactive props
const { baseColor, transitionDuration, transitionSpeed, noiseSettings } = toRefs(props);

// Current color state with transition
const currentColor = reactive({
  r: baseColor.value.r,
  g: baseColor.value.g,
  b: baseColor.value.b,
  targetR: baseColor.value.r,
  targetG: baseColor.value.g,
  targetB: baseColor.value.b,
  transitioning: false,
  transitionStartTime: 0,
});

// Current noise settings with transition
const currentNoise = reactive({
  scale: noiseSettings.value.scale,
  layers: noiseSettings.value.layers,
  power: noiseSettings.value.power,
  intensity: noiseSettings.value.intensity,
  targetScale: noiseSettings.value.scale,
  targetPower: noiseSettings.value.power,
  targetIntensity: noiseSettings.value.intensity,
  transitioning: false,
  transitionStartTime: 0,
});

// Compute full shader source with current color values
const fragmentShaderSource = computed(
  () => `
  precision mediump float;
  varying vec2 vUv;
  uniform float u_time;
  uniform float u_ratio;
  uniform vec2 u_pointer_position;
  uniform float u_scroll_progress; // Keep for compatibility but won't use
  uniform vec3 u_color; // Use uniform for color to allow transitions
  uniform float u_noise_scale;
  uniform float u_noise_power;
  uniform float u_noise_intensity;
  
  vec2 rotate(vec2 uv, float th) {
      return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
  }
  
  float neuro_shape(vec2 uv, float t, float p) {
      vec2 sine_acc = vec2(0.);
      vec2 res = vec2(0.);
      float scale = u_noise_scale; // Use the uniform scale
      for (int j = 0; j < ${currentNoise.layers}; j++) {
          uv = rotate(uv, 1.);
          sine_acc = rotate(sine_acc, 1.);
          vec2 layer = uv * scale + float(j) + sine_acc - t;
          sine_acc += sin(layer) + 2.4 * p;
          res += (.5 + .5 * cos(layer)) / scale;
          scale *= (1.2);
      }
      return res.x + res.y;
  }
  
  void main() {
      vec2 uv = .5 * vUv;
      uv.x *= u_ratio;
      vec2 pointer = vUv - u_pointer_position;
      pointer.x *= u_ratio;
      float p = clamp(length(pointer), 0., 1.);
      p = .5 * pow(1. - p, 2.);
      float t = .001 * u_time;
      vec3 color = vec3(0.);
      
      float noise = neuro_shape(uv, t, p);
      // Apply power and intensity with uniforms
      noise = u_noise_intensity * pow(noise, u_noise_power);
      noise += pow(noise, 10.);
      noise = max(.0, noise - .5);
      noise *= (1. - length(vUv - .5));
      
      // Use uniform color (will be updated during animation)
      color = normalize(u_color);
      
      color = color * noise;
      gl_FragColor = vec4(color, noise);
  }
`
);

// Vertex shader source remains the same
const vertexShaderSource = `
  precision mediump float;
  varying vec2 vUv;
  attribute vec2 a_position;
  void main() {
      vUv = .5 * (a_position + 1.);
      gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

// Refs
const canvasEl = ref(null);
let gl = null;
let uniforms = null;
let animationFrameId = null;

// Pointer state
const pointer = {
  x: 0,
  y: 0,
  tX: 0,
  tY: 0,
};

// Functions
function initShader() {
  // Use the computed shader source
  const vsSource = vertexShaderSource;
  const fsSource = fragmentShaderSource.value;

  const gl = canvasEl.value.getContext("webgl") || canvasEl.value.getContext("experimental-webgl");

  if (!gl) {
    console.error("WebGL is not supported by your browser.");
    return null;
  }

  function createShader(gl, sourceCode, type) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, sourceCode);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error("An error occurred compiling the shaders: " + gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  const vertexShader = createShader(gl, vsSource, gl.VERTEX_SHADER);
  const fragmentShader = createShader(gl, fsSource, gl.FRAGMENT_SHADER);

  function createShaderProgram(gl, vertexShader, fragmentShader) {
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Unable to initialize the shader program: " + gl.getProgramInfoLog(program));
      return null;
    }

    return program;
  }

  const shaderProgram = createShaderProgram(gl, vertexShader, fragmentShader);
  uniforms = getUniforms(shaderProgram);

  function getUniforms(program) {
    let uniformsObj = {};
    let uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < uniformCount; i++) {
      let uniformName = gl.getActiveUniform(program, i).name;
      uniformsObj[uniformName] = gl.getUniformLocation(program, uniformName);
    }
    return uniformsObj;
  }

  const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

  const vertexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

  gl.useProgram(shaderProgram);

  const positionLocation = gl.getAttribLocation(shaderProgram, "a_position");
  gl.enableVertexAttribArray(positionLocation);

  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

  // Initialize uniforms
  gl.uniform3f(uniforms.u_color, currentColor.r, currentColor.g, currentColor.b);
  gl.uniform1f(uniforms.u_noise_scale, currentNoise.scale);
  gl.uniform1f(uniforms.u_noise_power, currentNoise.power);
  gl.uniform1f(uniforms.u_noise_intensity, currentNoise.intensity);

  return gl;
}

function render() {
  if (!gl) return;

  const currentTime = performance.now();

  // Handle color transition if needed
  if (currentColor.transitioning) {
    const elapsedTime = currentTime - currentColor.transitionStartTime;
    const progress = Math.min(elapsedTime / transitionDuration.value, 1);

    if (progress < 1) {
      // Calculate interpolated color with improved easing
      // Use the transitionSpeed prop for the interpolation factor
      const easeFactor = transitionSpeed.value;
      currentColor.r = lerp(currentColor.r, currentColor.targetR, easeFactor);
      currentColor.g = lerp(currentColor.g, currentColor.targetG, easeFactor);
      currentColor.b = lerp(currentColor.b, currentColor.targetB, easeFactor);

      // If we're very close to the target, snap to it
      if (
        Math.abs(currentColor.r - currentColor.targetR) < 0.001 &&
        Math.abs(currentColor.g - currentColor.targetG) < 0.001 &&
        Math.abs(currentColor.b - currentColor.targetB) < 0.001
      ) {
        currentColor.r = currentColor.targetR;
        currentColor.g = currentColor.targetG;
        currentColor.b = currentColor.targetB;
        currentColor.transitioning = false;
      }
    } else {
      // Transition complete by timeout
      currentColor.r = currentColor.targetR;
      currentColor.g = currentColor.targetG;
      currentColor.b = currentColor.targetB;
      currentColor.transitioning = false;
    }

    // Update color uniform
    gl.uniform3f(uniforms.u_color, currentColor.r, currentColor.g, currentColor.b);
  }

  // Handle noise settings transition
  if (currentNoise.transitioning) {
    const elapsedTime = currentTime - currentNoise.transitionStartTime;
    const progress = Math.min(elapsedTime / transitionDuration.value, 1);

    if (progress < 1) {
      // Calculate interpolated noise settings with improved easing
      const easeFactor = transitionSpeed.value;
      currentNoise.scale = lerp(currentNoise.scale, currentNoise.targetScale, easeFactor);
      currentNoise.power = lerp(currentNoise.power, currentNoise.targetPower, easeFactor);
      currentNoise.intensity = lerp(
        currentNoise.intensity,
        currentNoise.targetIntensity,
        easeFactor
      );

      // If we're very close to the targets, snap to them
      if (
        Math.abs(currentNoise.scale - currentNoise.targetScale) < 0.01 &&
        Math.abs(currentNoise.power - currentNoise.targetPower) < 0.01 &&
        Math.abs(currentNoise.intensity - currentNoise.targetIntensity) < 0.01
      ) {
        currentNoise.scale = currentNoise.targetScale;
        currentNoise.power = currentNoise.targetPower;
        currentNoise.intensity = currentNoise.targetIntensity;
        currentNoise.transitioning = false;
      }
    } else {
      // Transition complete by timeout
      currentNoise.scale = currentNoise.targetScale;
      currentNoise.power = currentNoise.targetPower;
      currentNoise.intensity = currentNoise.targetIntensity;
      currentNoise.transitioning = false;
    }

    // Update noise uniforms
    gl.uniform1f(uniforms.u_noise_scale, currentNoise.scale);
    gl.uniform1f(uniforms.u_noise_power, currentNoise.power);
    gl.uniform1f(uniforms.u_noise_intensity, currentNoise.intensity);
  }

  pointer.x += (pointer.tX - pointer.x) * 0.2;
  pointer.y += (pointer.tY - pointer.y) * 0.2;

  gl.uniform1f(uniforms.u_time, currentTime);
  gl.uniform2f(
    uniforms.u_pointer_position,
    pointer.x / window.innerWidth,
    1 - pointer.y / window.innerHeight
  );
  gl.uniform1f(uniforms.u_scroll_progress, 0);

  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  animationFrameId = requestAnimationFrame(render);
}

// Helper function for color interpolation
function lerp(start, end, t) {
  return start + (end - start) * t;
}

function resizeCanvas() {
  if (!gl || !canvasEl.value) return;

  const devicePixelRatio = Math.min(window.devicePixelRatio, 2);

  // Set the canvas size in CSS pixels (for display)
  canvasEl.value.style.width = window.innerWidth + "px";
  canvasEl.value.style.height = window.innerHeight + "px";

  // Set the canvas buffer size in actual pixels (for rendering)
  canvasEl.value.width = Math.floor(window.innerWidth * devicePixelRatio);
  canvasEl.value.height = Math.floor(window.innerHeight * devicePixelRatio);

  gl.uniform1f(uniforms.u_ratio, canvasEl.value.width / canvasEl.value.height);
  gl.viewport(0, 0, canvasEl.value.width, canvasEl.value.height);
}

function updateMousePosition(eX, eY) {
  pointer.tX = eX;
  pointer.tY = eY;
}

// Event handler functions
const handlePointerMove = (e) => {
  updateMousePosition(e.clientX, e.clientY);
};

const handleTouchMove = (e) => {
  updateMousePosition(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
};

const handleClick = (e) => {
  updateMousePosition(e.clientX, e.clientY);
};

function setupEvents() {
  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("touchmove", handleTouchMove);
  window.addEventListener("click", handleClick);
  window.addEventListener("resize", resizeCanvas);
}

function cleanupEvents() {
  window.removeEventListener("pointermove", handlePointerMove);
  window.removeEventListener("touchmove", handleTouchMove);
  window.removeEventListener("click", handleClick);
  window.removeEventListener("resize", resizeCanvas);

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }
}

// Watch for prop changes to start transitions
watch([baseColor], () => {
  // Set target colors
  currentColor.targetR = baseColor.value.r;
  currentColor.targetG = baseColor.value.g;
  currentColor.targetB = baseColor.value.b;

  // Start transition
  currentColor.transitioning = true;
  currentColor.transitionStartTime = performance.now();
});

// Watch for noise setting changes
watch([noiseSettings], () => {
  // Set target noise settings
  currentNoise.targetScale = noiseSettings.value.scale;
  currentNoise.targetPower = noiseSettings.value.power;
  currentNoise.targetIntensity = noiseSettings.value.intensity;

  // Start transition
  currentNoise.transitioning = true;
  currentNoise.transitionStartTime = performance.now();
});

// Set initial values on mount
onMounted(() => {
  // Initialize current color with provided base color
  currentColor.r = baseColor.value.r;
  currentColor.g = baseColor.value.g;
  currentColor.b = baseColor.value.b;
  currentColor.targetR = baseColor.value.r;
  currentColor.targetG = baseColor.value.g;
  currentColor.targetB = baseColor.value.b;

  // Initialize noise settings
  currentNoise.scale = noiseSettings.value.scale;
  currentNoise.layers = noiseSettings.value.layers;
  currentNoise.power = noiseSettings.value.power;
  currentNoise.intensity = noiseSettings.value.intensity;
  currentNoise.targetScale = noiseSettings.value.scale;
  currentNoise.targetPower = noiseSettings.value.power;
  currentNoise.targetIntensity = noiseSettings.value.intensity;

  // Need to wait a tick for the canvas to be fully accessible
  setTimeout(() => {
    gl = initShader();
    if (gl) {
      setupEvents();
      resizeCanvas();
      render();
    }
  }, 0);
});

onBeforeUnmount(() => {
  cleanupEvents();
});
</script>

<style scoped>
.neuro-container {
  width: 100%;
  height: 100%;
}

#neuro {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.95;
}
</style>
